import { Button } from "@heroui/react";
import { type GenerationFailureReason, GenerationKind, RENDER_MODE_LABELS, RenderMode } from "@storyline/shared";
import AssetThumbnail from "@/src/common/components/asset-thumbnail";
import Pill from "@/src/common/components/pill";
import FrameSlot from "@/src/stories/components/frame-slot";
import RegenerateControl from "@/src/stories/components/regenerate-control";
import type { ShotFrameState } from "@/src/stories/utils/frame-state";
import type { OrderedShot } from "@/src/stories/utils/ordered-shots";

export type ContactMode = "keyframes" | "videos";

type ContactVignetteProps = {
	ordered: OrderedShot;
	state: ShotFrameState;
	mode: ContactMode;
	approved: boolean;
	videoBusy: boolean;
	imageCostUsd: number;
	videoCostUsd: number;
	onToggleApprove: () => void;
	onRegenerate: (kind: GenerationKind, reason: GenerationFailureReason) => void;
};

const ContactVignette: React.FC<ContactVignetteProps> = ({
	ordered,
	state,
	mode,
	approved,
	videoBusy,
	imageCostUsd,
	videoCostUsd,
	onToggleApprove,
	onRegenerate,
}) => {
	const { shot } = ordered;
	const isPremium = shot.renderMode === RenderMode.PREMIUM_VIDEO;
	const previewUrl = shot.startFrameUrl ?? state.entry.imageUrl ?? state.entry.inheritedFromUrl;

	return (
		<div className={`flex flex-col gap-2 rounded-xl border p-3 ${approved ? "border-success/50" : "border-border-soft"} bg-surface`}>
			<div className="flex items-start justify-between gap-2">
				<span className="min-w-0 break-words font-medium text-foreground text-xs">{ordered.label}</span>
				<Pill>{RENDER_MODE_LABELS[shot.renderMode]}</Pill>
			</div>
			<p className="line-clamp-2 text-muted text-xs">{shot.action}</p>
			{shot.endAction ? (
				<p className="line-clamp-2 text-muted text-xs">
					<span className="font-semibold text-foreground">Fin :</span> {shot.endAction}
				</p>
			) : null}

			{mode === "keyframes" ? (
				<>
					<div className={isPremium ? "grid grid-cols-2 gap-2" : "flex"}>
						<FrameSlot state={state.entry} caption="Entrée" costUsd={imageCostUsd} onRegenerate={onRegenerate} />
						{isPremium ? <FrameSlot state={state.exit} caption="Sortie" costUsd={imageCostUsd} onRegenerate={onRegenerate} /> : null}
					</div>
					{!isPremium ? (
						<span className="rounded-lg border border-border-soft border-dashed px-2 py-1 text-center text-muted text-xs">
							Frame de sortie non requise · plan non premium
						</span>
					) : null}
				</>
			) : (
				<div className="flex flex-col gap-1">
					{isPremium ? (
						<>
							<AssetThumbnail
								imageUrl={previewUrl}
								videoUrl={shot.videoUrl}
								isLoading={videoBusy}
								placeholder="Vidéo à générer"
								caption="Vidéo premium"
							/>
							<RegenerateControl
								isBusy={videoBusy}
								costUsd={videoCostUsd}
								onRegenerate={(reason) => onRegenerate(GenerationKind.SHOT_VIDEO, reason)}
							/>
						</>
					) : (
						<AssetThumbnail imageUrl={previewUrl} placeholder="Image fixe" caption="Pas de vidéo à générer" />
					)}
				</div>
			)}

			<Button size="sm" variant={approved ? "secondary" : "ghost"} className="w-full" onPress={onToggleApprove}>
				{approved ? "✓ Validé" : "Valider ce plan"}
			</Button>
		</div>
	);
};

export default ContactVignette;
